import { Middleware } from '@reduxjs/toolkit';
import {
  closePageView,
  hydrateViewStack,
  openPageView,
  setPageViewData,
  setupViewStack,
  updateViewBreadcrumbTitle,
} from '../reducers/pageViewsReducer';
import { StateModel } from '../models/state.model';
import { encodeState } from '../../utils/urlState';
import { closeSidebarView, openSidebar } from '../reducers/sidebarViewsReduser';

const PUSH = new Set([
  openPageView.type,
  closePageView.type,
  setupViewStack.type,
  openSidebar.type,
  closeSidebarView.type,
]);

const REPLACE = new Set([setPageViewData.type, updateViewBreadcrumbTitle.type]);

const uniqById = (items: any[] = []) => {
  const map = new Map();

  for (const item of items) {
    const key = item?.viewRequest?.id ?? item?.dataRequest?.id ?? JSON.stringify(item.viewRequest);

    map.set(key, item);
  }

  return [...map.values()];
};

export const navigationSyncMiddleware: Middleware<{}, StateModel> =
  (store) => (next) => (action) => {
    const result = next(action);

    if (action.type === hydrateViewStack.type) return result;

    if (!PUSH.has(action.type) && !REPLACE.has(action.type)) return result;

    const state = store.getState();

    if (!state.navigationSync.hydrated) return result;

    const stack = state.pageViewStack;
    if (!stack.length) return result;

    const lastIndex = stack.length - 1;

    const snapshot = [...stack];
    snapshot[lastIndex] = {
      ...snapshot[lastIndex],
      suspendedPopovers: {
        sidebars: uniqById(state.sidebarViewsState),
        dialogs: [],
      },
    };

    const url = new URL(window.location.href);
    const encoded = encodeState(snapshot);

    if (url.searchParams.get('s') === encoded) return result;

    url.searchParams.set('s', encoded);

    const shouldReplace =
      REPLACE.has(action.type) || (action as any)?.meta?.navigation === 'REPLACE';

    if (shouldReplace) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }

    return result;
  };
